import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '../auth'
import { flush, pullSnapshot, startSync } from '../lib/sync'
import ClientCard from './ClientCard'
import Clients from './Clients'
import { IconClients, IconClock, IconCloudOff, IconList, IconMoney } from './icons'
import Journal from './Journal'
import Money from './Money'
import NewTask from './NewTask'
import Profile from './Profile'
import TaskCard from './TaskCard'
import Today from './Today'
import { useSync } from './useLocal'
import './mobile.css'

type Tab = 'today' | 'journal' | 'clients' | 'money'
type Screen =
  | { name: 'tab' }
  | { name: 'task'; id: number }
  | { name: 'client'; id: number }
  | { name: 'new' }
  | { name: 'profile' }

const TABS: { key: Tab; label: string; Icon: typeof IconClock }[] = [
  { key: 'today', label: 'Сегодня', Icon: IconClock },
  { key: 'journal', label: 'Журнал', Icon: IconList },
  { key: 'clients', label: 'Клиенты', Icon: IconClients },
  { key: 'money', label: 'Деньги', Icon: IconMoney },
]

export default function MobileApp({ onLogout }: { onLogout: () => void | Promise<void> }) {
  const { user } = useAuth()
  const sync = useSync()

  const [tab, setTab] = useState<Tab>('today')
  const [screen, setScreen] = useState<Screen>({ name: 'tab' })
  const [toast, setToast] = useState<string | null>(null)
  const scroller = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!user) return
    const stop = startSync()
    void pullSnapshot()
    return stop
  }, [user])

  // Возврат из фона: телефон мог пролежать в кармане полдня.
  useEffect(() => {
    const onShow = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        void flush().then(() => pullSnapshot())
      }
    }
    document.addEventListener('visibilitychange', onShow)
    return () => document.removeEventListener('visibilitychange', onShow)
  }, [])

  const showToast = useCallback((text: string) => {
    setToast(text)
    window.setTimeout(() => setToast((t) => (t === text ? null : t)), 2200)
  }, [])

  const openTab = (t: Tab) => {
    setTab(t)
    setScreen({ name: 'tab' })
    scroller.current?.scrollTo({ top: 0 })
  }

  /**
   * Возврат из карточки — всегда через историю браузера, даже когда нажали
   * стрелку на экране. Иначе запись в истории остаётся, и следующее нажатие
   * аппаратной кнопки «назад» уводит из программы вместо предыдущего экрана.
   */
  const back = () => window.history.back()

  // Аппаратная кнопка «назад» на Android должна закрывать карточку, а не
  // приложение: иначе техник вылетает из программы посреди работы.
  useEffect(() => {
    if (screen.name === 'tab') return
    window.history.pushState({ m: 1 }, '')
    const onPop = () => setScreen({ name: 'tab' })
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [screen.name])

  let body
  if (screen.name === 'task') {
    body = <TaskCard taskId={screen.id} onBack={back} onToast={showToast} />
  } else if (screen.name === 'client') {
    body = <ClientCard clientId={screen.id} onBack={back} onToast={showToast} />
  } else if (screen.name === 'new') {
    body = <NewTask onBack={back} onToast={showToast} />
  } else if (screen.name === 'profile') {
    body = <Profile onBack={back} onLogout={onLogout} onToast={showToast} />
  } else if (tab === 'today') {
    body = (
      <Today
        onOpenTask={(id) => setScreen({ name: 'task', id })}
        onNewTask={() => setScreen({ name: 'new' })}
        onAllTasks={() => openTab('journal')}
        onProfile={() => setScreen({ name: 'profile' })}
      />
    )
  } else if (tab === 'journal') {
    body = (
      <Journal
        onOpenTask={(id) => setScreen({ name: 'task', id })}
        onNewTask={() => setScreen({ name: 'new' })}
      />
    )
  } else if (tab === 'clients') {
    body = <Clients onOpen={(id) => setScreen({ name: 'client', id })} />
  } else {
    body = <Money onOpenTask={(id) => setScreen({ name: 'task', id })} />
  }

  const showNav = screen.name === 'tab'

  return (
    <div className="m-app">
      {(!sync.online || sync.pending > 0) && (
        <div className="m-offline">
          <IconCloudOff />
          {!sync.online
            ? sync.pending > 0
              ? `Нет сети. Не отправлено: ${sync.pending}`
              : 'Нет сети. Данные показаны из памяти телефона'
            : `Отправляем: ${sync.pending}`}
        </div>
      )}

      <div className="m-scroll" ref={scroller} style={showNav ? undefined : { paddingBottom: 0 }}>
        {body}
      </div>

      {showNav && (
        <div className="m-nav">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} className={tab === key ? 'on' : ''} onClick={() => openTab(key)}>
              <Icon color={tab === key ? '#053d5e' : '#8195a5'} />
              {label}
            </button>
          ))}
        </div>
      )}

      {toast && <div className="m-toast">{toast}</div>}
    </div>
  )
}
